import { useEffect, useId, useMemo, useRef, useState } from "react";
import { leakByYear, money } from "../lib/calc";

type Props = {
  yearlySave: number;
  years: number;
};

export function LeakChart({ yearlySave, years }: Props) {
  const titleId = useId();
  const descId = useId();
  const svgRef = useRef<SVGSVGElement>(null);
  const points = useMemo(() => leakByYear(yearlySave, years), [yearlySave, years]);
  const [index, setIndex] = useState(() => Math.min(5, Math.max(0, Math.round(years))));

  useEffect(() => {
    setIndex((n) => Math.min(n, points.length - 1));
  }, [points.length]);
  const i = Math.min(index, points.length - 1);
  const point = points[i] ?? points[0];

  const w = 320;
  const h = 100;
  const padL = 36;
  const padR = 10;
  const padT = 8;
  const padB = 20;
  const maxY = Math.max(1, points[points.length - 1]?.extra ?? 1);
  const innerW = w - padL - padR;
  const innerH = h - padT - padB;
  const last = points[points.length - 1]?.year || 1;

  function x(year: number) {
    return padL + (year / last) * innerW;
  }
  function y(extra: number) {
    return padT + innerH - (extra / maxY) * innerH;
  }

  const d = points
    .map((p, n) => `${n === 0 ? "M" : "L"} ${x(p.year).toFixed(1)} ${y(p.extra).toFixed(1)}`)
    .join(" ");
  const area = `${d} L ${x(last).toFixed(1)} ${(padT + innerH).toFixed(1)} L ${x(0).toFixed(1)} ${(padT + innerH).toFixed(1)} Z`;
  const grids = [0, 0.5, 1];

  function setFromClientX(clientX: number) {
    const svg = svgRef.current;
    if (!svg) return;
    const rect = svg.getBoundingClientRect();
    const t01 = (clientX - rect.left - (padL / w) * rect.width) / ((innerW / w) * rect.width);
    setIndex(Math.round(Math.min(1, Math.max(0, t01)) * (points.length - 1)));
  }

  return (
    <figure className="plot plot-scrub">
      <div className="plot-readout" aria-live="polite">
        <strong>{money(point.extra)}</strong>
        <span>
          extra in {point.year} {point.year === 1 ? "year" : "years"} if the gap stays
        </span>
      </div>
      <svg
        ref={svgRef}
        viewBox={`0 0 ${w} ${h}`}
        role="slider"
        tabIndex={0}
        aria-labelledby={titleId}
        aria-describedby={descId}
        aria-valuemin={0}
        aria-valuemax={points.length - 1}
        aria-valuenow={i}
        aria-valuetext={`${point.year} years, ${money(point.extra)} extra. Drag or use arrow keys.`}
        onPointerDown={(e) => {
          e.currentTarget.setPointerCapture(e.pointerId);
          setFromClientX(e.clientX);
        }}
        onPointerMove={(e) => {
          if (e.currentTarget.hasPointerCapture(e.pointerId)) setFromClientX(e.clientX);
        }}
        onKeyDown={(e) => {
          if (e.key === "ArrowRight" || e.key === "ArrowUp") {
            e.preventDefault();
            setIndex((n) => Math.min(points.length - 1, n + 1));
          }
          if (e.key === "ArrowLeft" || e.key === "ArrowDown") {
            e.preventDefault();
            setIndex((n) => Math.max(0, n - 1));
          }
        }}
      >
        <title id={titleId}>Extra repayments if you keep this rate</title>
        <desc id={descId}>
          Drag or use arrow keys. Values are the difference in scheduled repayments, not a forecast
          of future market rates.
        </desc>
        {grids.map((g) => (
          <g key={g}>
            <line
              x1={padL}
              y1={y(maxY * g)}
              x2={w - padR}
              y2={y(maxY * g)}
              className="plot-grid"
            />
            <text x={padL - 6} y={y(maxY * g) + 4} className="plot-axis-label" textAnchor="end">
              {g === 0 ? "0" : g === 1 ? compact(maxY) : ""}
            </text>
          </g>
        ))}
        <path d={area} className="plot-fill you" />
        <path d={d} className="plot-line you" />
        <line x1={x(point.year)} y1={padT} x2={x(point.year)} y2={padT + innerH} className="plot-play" />
        <circle cx={x(point.year)} cy={y(point.extra)} r={6} className="plot-you-dot" />
        <text x={padL} y={h - 4} className="plot-axis-label">
          Now
        </text>
        <text x={w - padR} y={h - 4} className="plot-axis-label" textAnchor="end">
          {last} yrs
        </text>
      </svg>
    </figure>
  );
}

function compact(n: number): string {
  if (n >= 1000) return `${Math.round(n / 1000)}k`;
  return String(Math.round(n));
}
